// facets.js - instance-aware version for custom blog-filters UI
// Note: must be included once (defer) on the page

/* Utility debounce (kept same) */
function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function onKeyUpEscape(e) {
  if (e.key === 'Escape' || e.key === 'Esc') {
    const details = e.target.closest('details');
    if (details) details.removeAttribute('open');
    e.target.blur();
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    // instance-bound handlers
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // debounce per-instance
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      // use input event for checkboxes/range and text input
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      // also listen for change on selects (if any)
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    // Don't rely on a fixed ID for wrapper; use class selector so it works in mobile/desktop instances.
    const facetWrapper = this.querySelector('.blog-filters__items');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // wire up any active facet remove links inside this form
    this.querySelectorAll('.js-facet-remove, facet-remove a, .active-facets__button').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.onActiveFilterClick(e);
      });
      el.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.onActiveFilterClick(event);
        }
      });
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const grid = document.getElementById('ProductGridContainer');
    if (grid) grid.querySelector('.collection').classList.add('loading');

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        // optional hook if your theme has a scroll animation initializer
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newGrid = doc.getElementById('ProductGridContainer');
    if (!newGrid) return;
    document.getElementById('ProductGridContainer').innerHTML = newGrid.innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const count = doc.getElementById('ProductCount') ? doc.getElementById('ProductCount').innerHTML : null;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (count && container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (count && containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // gather all js-filter elements from server response for the known forms
    const selector = [
      '#FacetFiltersForm .js-filter',
      '#FacetFiltersFormMobile .js-filter',
      '#FacetFiltersFormMobile .js-filter',
      '#FacetFiltersPillsForm .js-filter',
    ].join(',');

    // But our snippet uses suffixed IDs; select anything inside forms with class .facets (.js-filter inside any facets form)
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '.facets .js-filter, .blog-filters__items .js-filter, form[id^="FacetFiltersForm"] .js-filter',
    );

    const facetDetailsElementsFromDom = document.querySelectorAll(
      '.facets .js-filter, .blog-filters__items .js-filter, form[id^="FacetFiltersForm"] .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            const prevEl = document.getElementById(previousElementId);
            if (prevEl) {
              prevEl.after(elementToRender);
              return;
            }
          }
        }

        if (elementToRender.parentElement) {
          const parentId = elementToRender.parentElement.id;
          const parent = document.getElementById(parentId);
          if (parent && parent.querySelector('.js-filter')) {
            parent.querySelector('.js-filter').before(elementToRender);
          } else if (parent) {
            parent.appendChild(elementToRender);
          } else {
            // fallback append to first facets wrapper
            const fallback = document.querySelector('.blog-filters__items');
            if (fallback) fallback.appendChild(elementToRender);
          }
        }
      }
    });

    // render active facets (mobile/desktop)
    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender && event && event.target) {
      const jsFilter = event.target.closest('.js-filter');
      if (jsFilter) {
        FacetFiltersForm.renderCounts(countsToRender, jsFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, jsFilter);

        const newFacetDetailsElement = document.getElementById(jsFilter.id);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    // Update both desktop & mobile active facets independently if present
    const activeDesktop = html.querySelector('.active-facets-desktop');
    if (activeDesktop) {
      const el = document.querySelector('.active-facets-desktop');
      if (el) el.innerHTML = activeDesktop.innerHTML;
    }

    const activeMobile = html.querySelector('.active-facets-mobile');
    if (activeMobile) {
      const elm = document.querySelector('.active-facets-mobile');
      if (elm) elm.innerHTML = activeMobile.innerHTML;
    }

    FacetFiltersForm.toggleActiveFacets(false);

    // re-bind active facet remove links (they may be newly inserted)
    document.querySelectorAll('.js-facet-remove, facet-remove a, .active-facets__button').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const form = document.querySelector('facet-filters-form') || document.querySelector('facet-filters-form');
        if (form && form.onActiveFilterClick) form.onActiveFilterClick(e);
      });
    });
  }

  static renderCounts(source, target) {
    const sourceSummary = source.querySelector('.facets__summary');
    const targetSummary = target.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const sourceHeaderElement = source.querySelector('.facets__header');
    const targetHeaderElement = target.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const sourceWrapElement = source.querySelector('.facets-wrap');
    const targetWrapElement = target.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const sourceFacetsList = source.querySelector('.mobile-facets__list, .facets__list');
    const targetFacetsList = target.querySelector('.mobile-facets__list, .facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    // fallback to #product-grid data-id
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return [];
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (!event) return;

    event.preventDefault();
    const target = event.target;

    // Desktop checkbox detection
    const isDesktopCheckbox = target && target.tagName === 'INPUT' && target.closest('form')?.id === 'FacetFiltersForm';

    // Mobile checkbox detection
    const isMobileCheckbox =
      target && target.tagName === 'INPUT' && target.closest('form')?.id === 'FacetFiltersFormMobile';

    if (isMobileCheckbox) {
      // MOBILE: only submit the mobile form
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm) {
        const searchParams = this.createSearchParams(mobileForm);
        this.onSubmitForm(searchParams, event);
      } else {
        console.warn('Mobile form not found.');
      }
    } else if (isDesktopCheckbox) {
      // DESKTOP: submit desktop forms
      const desktopForm = document.getElementById('FacetFiltersForm');
      if (desktopForm) {
        const searchParams = this.createSearchParams(desktopForm);
        this.onSubmitForm(searchParams, event);
      } else {
        console.warn('Desktop form not found.');
      }
    } else {
      console.warn('Clicked element not recognized as a filter checkbox.');
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget && event.currentTarget.href ? event.currentTarget.href : '';
    const url = href.indexOf('?') == -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* price-range and facet-remove components are assumed to be present in your theme already.
   If you had customised them, keep those custom files. */
